import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Icon } from 'Components/atoms/Icon';
import { withPolaris } from 'Components/protons/Polaris';

import './_style.scss';

/**
 * StatusIndicatorBadge component
 * @param {Object} props - The properties object.
 * @param {string} props.context - The context object containing additional information.
 * @param {string} props.extraClassNames - The extra CSS classes to apply.
 * @param {string} props.icon - The icon to display in the badge.
 * @param {string} props.text - The text to display in the badge.
 * @returns {React.ReactElement} - The rendered Badge component.
 * @constructor
 * @name StatusIndicatorBadge
 * @example
 * <StatusIndicatorBadge status="success" icon="icon" text="text" />
 */

export const StatusIndicatorBadge = ({
  context: { cssPrefix },
  extraClassNames,
  icon,
  status,
  statusValue,
  text,
}) => {
  if (!status) {
    return null;
  }

  return (
    <div
      className={classNames({
        [`${cssPrefix}__status-indicator-badge`]: true,
        [`-${status}`]: status,
        ...extraClassNames,
      })}
      role="status"
      // set aria attributes only if statusValue is present
      aria-live={statusValue ? 'polite' : undefined}
      aria-label={statusValue || undefined}
    >
      {icon && <Icon icon={`${cssPrefix}__${icon}`} />}
      {text}
    </div>
  );
};

StatusIndicatorBadge.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape(),
  icon: PropTypes.string,
  text: PropTypes.string,
  status: PropTypes.string,
  statusValue: PropTypes.string,
};

StatusIndicatorBadge.defaultProps = {
  context: {},
  extraClassNames: {},
  icon: '',
  text: '',
  status: '',
  statusValue: '',
};

export default withPolaris(StatusIndicatorBadge);
